import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Import Lucide Icons
import {
    LayoutDashboard, Bot, Mail, // Sidebar
    Menu, Search, Globe, LifeBuoy, Bell, ChevronDown, // Topbar
    BarChart3, // Title Icon
    Calendar, // Date Range
    Loader2, // Loading
    ShieldAlert, // Error
    Info, // Info messages
    CheckCircle // Selected Agent Indicator (Optional)
} from 'lucide-react';

// --- Animation Utility Hook ---
const useOnScreen = (options = { threshold: 0.1 }) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') { setIsVisible(true); return; }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setIsVisible(true); /* observer.unobserve(entry.target); */ }
            // else { setIsVisible(false); } // Optional: Reset on scroll out
        }, options);
        const currentRef = ref.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef && observer) observer.unobserve(currentRef); };
    }, [options]);
    const hookRef = (node) => { ref.current = node; };
    return [hookRef, isVisible];
};

// --- Styled Clickable Components ---
const ClickableLink = ({ children, className, href = '#', target, rel, onClick, disabled, title, ...props }) => (
    <a href={!disabled ? href : undefined} target={target} rel={target === '_blank' ? 'noopener noreferrer' : rel} onClick={!disabled ? onClick : (e) => e.preventDefault()} role="button" tabIndex={disabled ? -1 : 0} className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`} aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props}>{children}</a>
);
const ClickableDiv = ({ children, className, onClick, disabled, title, ...props }) => (
    <div role="button" tabIndex={disabled ? -1 : 0} onClick={!disabled ? onClick : undefined} onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }} className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`} aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props}>{children}</div>
);

// --- Sidebar Nav Item ---
const SidebarNavItem = ({ icon: Icon, text, href, active, onClick }) => (
     <a href={href} onClick={onClick} className={`flex items-center pl-3 pr-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 group relative ${ active ? 'bg-gray-800/60 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50' }`}>
         <Icon className={`w-4 h-4 mr-2.5 flex-shrink-0 ${active ? 'text-purple-400' : 'text-gray-500 group-hover:text-gray-300'}`} strokeWidth={1.5} />
         <span className="truncate">{text}</span>
     </a>
);

// --- Sidebar ---
const Sidebar = () => {
    const navigate = useNavigate();
    const { user, isLoaded } = useUser();

    const handleNavigate = (e, path) => {
        e.preventDefault();
        navigate(path);
    };

    const userEmail = isLoaded && user ? user.primaryEmailAddress?.emailAddress : 'Loading...';

    return (
        <div className="w-52 md:w-56 h-screen bg-black border-r border-gray-800/50 flex flex-col flex-shrink-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black">
            {/* Header */}
            <div className="h-14 flex items-center px-3 flex-shrink-0 border-b border-gray-800/50">
                <div className="flex items-center">
                    <svg className="w-6 h-6 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#8B5CF6" stroke="#8B5CF6" strokeWidth="1.5"/>
                        <path d="M2 17L12 22L22 17" stroke="#8B5CF6" strokeWidth="1.5"/>
                        <path d="M2 12L12 17L22 12" stroke="#8B5CF6" strokeWidth="1.5"/>
                    </svg>
                    <span className="text-base font-bold text-white">Echidna Foundry</span>
                </div>
            </div>

            {/* Navigation */}
            <div className="flex-grow py-2">
                <nav className="space-y-0.5 px-2 mt-3">
                    <SidebarNavItem
                        key="back-dashboard"
                        icon={LayoutDashboard}
                        text="Back to Dashboard"
                        href="/echidna/efoundry/dashboard"
                        active={false}
                        onClick={(e) => handleNavigate(e, '/echidna/efoundry/dashboard')}
                    />
                     <SidebarNavItem
                         key="back-assistants"
                         icon={Bot}
                         text="My Assistants"
                         href="/echidna/myassistants"
                         active={false} // Make active based on current route if needed
                         onClick={(e) => handleNavigate(e, '/echidna/myassistants')}
                     />
                     {/* You could make the 'Analytics' link active here */}
                     <SidebarNavItem
                         key="analytics"
                         icon={BarChart3}
                         text="Analytics"
                         href="/echidna/stats" // Assuming this page's route
                         active={true} // Set active for this page
                         onClick={(e) => handleNavigate(e, '/echidna/stats')}
                     />
                </nav>
            </div>

            {/* User Info */}
            <div className="p-2 border-t border-gray-800/50 mt-auto">
                <ClickableLink
                    href="#profile" title={userEmail}
                    className="flex items-center p-2 rounded-md text-xs text-gray-400 hover:bg-gray-800/50 hover:text-white group w-full"
                 >
                    <Mail className="w-4 h-4 mr-2 text-gray-500 group-hover:text-gray-300 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate font-medium">{userEmail}</span>
                </ClickableLink>
            </div>
        </div>
    );
};

// --- Top Bar ---
const TopBar = ({ pageTitle }) => {
    const { user } = useUser();
    const userInitial = user?.firstName?.[0] || user?.username?.[0] || user?.primaryEmailAddress?.emailAddress?.[0] || '?';
    const userDisplayName = user?.firstName ? `${user.firstName}${user.lastName ? ' ' + user.lastName[0] + '.' : ''}` : user?.username || user?.primaryEmailAddress?.emailAddress || 'Account';

    return (
        <div className="h-14 bg-gray-950 border-b border-gray-800/50 flex items-center justify-between px-4 flex-shrink-0 sticky top-0 z-20">
             <div className="flex items-center space-x-3"><ClickableDiv className="p-1.5 -m-1.5 rounded hover:bg-gray-800/50 text-gray-300 hover:text-white" title="Services Menu"><Menu className="w-5 h-5" strokeWidth={1.5}/></ClickableDiv><span className="text-sm font-medium text-white">{pageTitle}</span></div>
             <div className="flex items-center space-x-3 md:space-x-4"><div className="relative hidden md:block"><Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={1.5}/><input type="search" placeholder={`Search ${pageTitle}...`} className="bg-gray-800/60 border border-gray-700/50 rounded-md pl-8 pr-2 py-1 w-48 lg:w-64 text-xs text-gray-200 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none focus:bg-gray-800" /></div><ClickableDiv className="flex items-center text-xs text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50" title="Region"><Globe className="w-4 h-4 mr-1" strokeWidth={1.5}/><span>us-east-1</span></ClickableDiv><ClickableLink href="#help" title="Support" className="text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50"><LifeBuoy className="w-5 h-5" strokeWidth={1.5}/></ClickableLink><ClickableDiv title="Notifications" className="text-gray-400 hover:text-white relative p-1.5 -m-1.5 rounded hover:bg-gray-800/50"><Bell className="w-5 h-5" strokeWidth={1.5}/></ClickableDiv>
             <ClickableDiv title="Account" className="flex items-center text-xs text-gray-300 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                 <span className="hidden md:inline mr-1 truncate max-w-[100px]" title={userDisplayName}>{userDisplayName}</span>
                 <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">{userInitial.toUpperCase()}</span>
                 <ChevronDown className="w-4 h-4 ml-1 text-gray-500" strokeWidth={1.5}/>
             </ClickableDiv>
             </div>
         </div>
    );
};


// --- Stats Content Component (Restored Agent Selection) ---
const StatsContent = () => {
  const { user } = useUser();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [apiStats, setApiStats] = useState(null);
  const [componentStats, setComponentStats] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [agentError, setAgentError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });

  // Animation Hooks
  const [headerRef, isHeaderVisible] = useOnScreen();
  const [agentSelectRef, isAgentSelectVisible] = useOnScreen();
  const [dateRangeRef, isDateRangeVisible] = useOnScreen();
  const [summaryRef, isSummaryVisible] = useOnScreen();
  const [chartsRef, isChartsVisible] = useOnScreen();
  const [metricsRef, isMetricsVisible] = useOnScreen();

  // Fetch user's agents on component mount
  useEffect(() => {
    if (user) {
      const fetchAgents = async () => {
        setLoadingAgents(true);
        setAgentError(null);
        setAgents([]); // Clear previous agents
        setSelectedAgent(null); // Deselect agent when user changes or refetches
        setApiStats(null);
        setComponentStats(null);
        try {
          const cid = user.id;
          console.log(`Fetching agents for CID: ${cid}`);
          // ** USE PRODUCTION URL **
          const response = await axios.post('https://web-production-5ee8.up.railway.app/echidna/myagents', { cid });
          // ** USE LOCAL URL for testing **
          // const response = await axios.post('http://localhost:5001/echidna/myagents', { cid });
          console.log("Agents fetched:", response.data);
          setAgents(response.data.agents || []);
           if (!response.data.agents || response.data.agents.length === 0) {
               // This is not necessarily an error, just info
               console.log("No agents found for this user.");
           }
        } catch (err) {
          setAgentError('Failed to fetch your assistants. Please try again later.');
          console.error('Error fetching agents:', err);
          setAgents([]); // Ensure agents is empty on error
        } finally {
          setLoadingAgents(false);
        }
      };
      fetchAgents();
    } else {
        // Handle case where user is not loaded yet or logs out
        setLoadingAgents(false);
        setAgents([]);
        setSelectedAgent(null);
        setApiStats(null);
        setComponentStats(null);
        setAgentError(null); // Clear error if user context is missing
    }
  }, [user]); // Re-fetch if user changes

  // Fetch stats when an agent is selected or date range changes
  const fetchStats = useCallback(async () => {
    if (!selectedAgent || !user) return; // Don't fetch if no agent selected or no user

    setLoadingStats(true);
    setStatsError(null);
    setApiStats(null); // Clear previous stats
    setComponentStats(null);

    try {
      const cid = user.id;
      const { startDate, endDate } = dateRange;
      const agentId = selectedAgent._id;
      console.log(`Fetching stats for Agent ID: ${agentId}, CID: ${cid}, Range: ${startDate} to ${endDate}`);

      // ** USE PRODUCTION URLs **
      const apiPromise = axios.post('https://web-production-5ee8.up.railway.app/echidna/agentinteractionstats', {
        agentId, cid, startDate, endDate
      });
      const componentPromise = axios.post('https://web-production-5ee8.up.railway.app/echidna/componentinteractionstats', {
        agentId, clientId: cid, startDate, endDate // Use clientId as per backend
      });

      // ** USE LOCAL URLs for testing **
      // const apiPromise = axios.post('http://localhost:5001/echidna/agentinteractionstats', {
      //   agentId, cid, startDate, endDate
      // });
      // const componentPromise = axios.post('http://localhost:5001/echidna/componentinteractionstats', {
      //   agentId, clientId: cid, startDate, endDate
      // });

      const [apiResponse, componentResponse] = await Promise.all([apiPromise, componentPromise]);

      console.log("API Stats response:", apiResponse.data);
      console.log("Component Stats response:", componentResponse.data);
      setApiStats(apiResponse.data);
      setComponentStats(componentResponse.data);

    } catch (err) {
      setStatsError(err.response?.data?.message || err.message || 'Failed to fetch statistics. Please try again later.');
      console.error('Error fetching stats:', err);
      setApiStats(null); // Clear stats on error
      setComponentStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, [selectedAgent, dateRange, user]); // Dependencies for fetching stats

  // Trigger fetchStats when dependencies change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);


  // Prepare chart data (robust date sorting)
  const prepareChartData = useCallback(() => {
    if (!apiStats || !componentStats) return [];
    const apiDaily = apiStats.dailyStats || [];
    const componentDaily = componentStats.dailyStats || [];
    const apiStatsMap = apiDaily.reduce((acc, item) => { acc[item.day] = item; return acc; }, {});
    const componentStatsMap = componentDaily.reduce((acc, item) => { acc[item.day] = item; return acc; }, {});
    const allDaysSet = new Set([...apiDaily.map(item => item.day), ...componentDaily.map(item => item.day)]);

    const sortedDays = Array.from(allDaysSet).sort((a, b) => {
        const parseDate = (dayStr) => {
            try {
                const date = new Date(dayStr + ", " + new Date().getFullYear());
                return !isNaN(date.getTime()) ? date : new Date(0);
            } catch { return new Date(0); }
        };
        return parseDate(a) - parseDate(b);
    });

    return sortedDays.map(day => ({
        day,
        apiMessages: apiStatsMap[day]?.apiMessages || 0,
        componentMessages: componentStatsMap[day]?.componentMessages || 0,
        apiCost: apiStatsMap[day]?.apiCost || 0,
        componentCost: componentStatsMap[day]?.componentCost || 0
    }));
  }, [apiStats, componentStats]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 6
    }).format(amount || 0);
  };

  const chartData = prepareChartData();

  // Calculate total cost safely
  const totalCost = (apiStats?.totalCostUSD || 0) + (componentStats?.totalCostUSD || 0);
  const totalConversations = (apiStats?.totalConversations || 0) + (componentStats?.totalConversations || 0);
  const totalMessages = (apiStats?.totalMessages || 0) + (componentStats?.totalMessages || 0);

  // Determine Agent Name for display
  const selectedAgentName = selectedAgent?.agentName || 'Selected Agent';

  return (
    <div className="p-6 lg:p-8 h-full">
        {/* Header */}
        <header
            ref={headerRef}
            className={`flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4 transition-all duration-500 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
        >
            <div>
                <div className="flex items-center gap-3 mb-1">
                    <BarChart3 className="w-7 h-7 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
                    <h1 className="text-3xl font-bold tracking-tight text-gray-100">Assistant Analytics</h1>
                </div>
                <p className="text-base text-gray-400 sm:ml-10">
                   {selectedAgent ? `Viewing statistics for: ${selectedAgentName}` : 'Select an assistant below to view its statistics.'}
                </p>
            </div>
        </header>

        {/* Agent Selection Section */}
        <section
            ref={agentSelectRef}
            className={`mb-12 transition-all duration-500 ease-out delay-100 ${isAgentSelectVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
        >
            <h2 className="text-xl font-semibold text-gray-200 mb-5 border-b border-gray-800/50 pb-2">
                Select Assistant
            </h2>

            {/* Agent Loading State */}
            {loadingAgents && (
                <div className="flex items-center justify-center py-8 text-gray-500">
                    <Loader2 className="w-6 h-6 mr-2 animate-spin" /> Loading your assistants...
                </div>
            )}

            {/* Agent Fetching Error */}
            {!loadingAgents && agentError && (
                <div className="p-4 border border-red-700/50 bg-gray-950 rounded-lg">
                    <p className="text-sm text-red-400 flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                        <span>{agentError}</span>
                    </p>
                </div>
            )}

            {/* No Agents Found */}
            {!loadingAgents && !agentError && agents.length === 0 && (
                <div className="text-center py-10 px-6 bg-gray-950 border border-dashed border-gray-800/50 rounded-lg">
                    <Info className="w-10 h-10 text-gray-700 mx-auto mb-3" strokeWidth={1}/>
                    <p className="text-gray-500">You haven't created any assistants yet.</p>
                    <p className="text-xs text-gray-600 mt-1">Create one from the 'My Assistants' page to see statistics.</p>
                </div>
            )}

            {/* Agent Grid */}
            {!loadingAgents && !agentError && agents.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {agents.map(agent => (
                    <ClickableDiv
                        key={agent._id}
                        onClick={() => setSelectedAgent(agent)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black ${
                        selectedAgent && selectedAgent._id === agent._id
                            ? 'border-purple-500 bg-gray-900 shadow-lg ring-1 ring-purple-500/80' // Selected state
                            : 'border-gray-800/70 bg-gray-950 hover:border-gray-700 hover:bg-gray-900/70' // Default state
                        }`}
                        title={`Select ${agent.agentName}`}
                    >
                         <div className="flex flex-col h-full">
                            <div className="flex justify-between items-start mb-2">
                                <h3 className={`text-base font-semibold truncate ${selectedAgent?._id === agent._id ? 'text-purple-300' : 'text-gray-200'}`}>
                                    {agent.agentName}
                                </h3>
                                {selectedAgent?._id === agent._id && (
                                    <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                )}
                            </div>
                            <div className="text-xs text-gray-500 space-y-0.5 mt-auto">
                                <p className="font-mono truncate" title={agent._id}>ID: ...{agent._id.slice(-8)}</p>
                                <p>Created: {new Date(agent.createdAt).toLocaleDateString()}</p>
                            </div>
                        </div>
                    </ClickableDiv>
                    ))}
                </div>
            )}
        </section>

        {/* Stats Display Area (Conditional on Agent Selection) */}
        {selectedAgent && (
            <div className="space-y-12">
                {/* Date Range Selection */}
                 <section
                    ref={dateRangeRef}
                    className={`transition-all duration-500 ease-out delay-150 ${isDateRangeVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                 >
                    <h2 className="text-lg font-semibold text-gray-300 mb-3 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-gray-500" strokeWidth={1.5}/>
                        Select Date Range
                    </h2>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        <div className="flex items-center gap-2">
                            <label htmlFor="startDate" className="block text-sm text-gray-400 flex-shrink-0">Start:</label>
                            <input
                                id="startDate" type="date" value={dateRange.startDate}
                                onChange={e => setDateRange({...dateRange, startDate: e.target.value})}
                                className="bg-gray-900/70 border border-gray-700/50 p-1.5 px-2 text-sm text-gray-200 rounded-md focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600"
                                aria-label="Start Date"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label htmlFor="endDate" className="block text-sm text-gray-400 flex-shrink-0">End:</label>
                            <input
                                id="endDate" type="date" value={dateRange.endDate}
                                onChange={e => setDateRange({...dateRange, endDate: e.target.value})}
                                className="bg-gray-900/70 border border-gray-700/50 p-1.5 px-2 text-sm text-gray-200 rounded-md focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600"
                                aria-label="End Date" max={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </div>
                </section>

                 {/* Loading State for Stats */}
                 {loadingStats && (
                    <div className="flex justify-center items-center h-64">
                        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                        <p className="ml-3 text-gray-400">Loading statistics for {selectedAgentName}...</p>
                    </div>
                 )}

                 {/* Error Display for Stats */}
                 {!loadingStats && statsError && (
                    <div className="mb-8 p-4 border border-red-700/50 bg-gray-950 rounded-lg">
                        <p className="text-sm text-red-400 flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                            <span>{statsError}</span>
                        </p>
                    </div>
                 )}

                 {/* Actual Stats Data */}
                 {!loadingStats && !statsError && apiStats && componentStats && (
                    <>
                        {/* Summary Stats */}
                        <section ref={summaryRef} className={`transition-all duration-500 ease-out delay-200 ${isSummaryVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                             <h2 className="text-xl font-semibold text-gray-200 mb-5 border-b border-gray-800/50 pb-2">
                                 Overall Summary <span className="text-sm font-normal text-gray-500">({dateRange.startDate} to {dateRange.endDate})</span>
                             </h2>
                             {/* ... (Summary Grid - same as previous correct version) ... */}
                             <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-sm font-medium text-gray-400 mb-1">Total Conversations</h3>
                                     <p className="text-3xl font-semibold text-gray-100">{totalConversations.toLocaleString()}</p>
                                 </div>
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-sm font-medium text-gray-400 mb-1">Total Messages</h3>
                                     <p className="text-3xl font-semibold text-gray-100">{totalMessages.toLocaleString()}</p>
                                 </div>
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-sm font-medium text-gray-400 mb-1">Total Estimated Cost</h3>
                                     <p className="text-3xl font-semibold text-gray-100">{formatCurrency(totalCost)}</p>
                                 </div>
                             </div>
                             {/* Detailed Breakdown */}
                             <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-lg font-semibold text-purple-300 mb-4">API Statistics</h3>
                                     <ul className="space-y-2 text-sm">
                                        <li className="flex justify-between"><span className="text-gray-400">Conversations:</span> <span className="text-gray-200 font-medium">{apiStats.totalConversations?.toLocaleString() || 0}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">Total Messages:</span> <span className="text-gray-200 font-medium">{apiStats.totalMessages?.toLocaleString() || 0}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">User Messages:</span> <span className="text-gray-200 font-medium">{apiStats.totalUserMessages?.toLocaleString() || 0}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">Agent Messages:</span> <span className="text-gray-200 font-medium">{apiStats.totalAgentMessages?.toLocaleString() || 0}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">Est. Cost (USD):</span> <span className="text-gray-200 font-medium">{formatCurrency(apiStats.totalCostUSD)}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">Msg Rate:</span> <span className="text-gray-200 font-medium">{apiStats.messageRate || 'N/A'}</span></li>
                                     </ul>
                                 </div>
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-lg font-semibold text-indigo-300 mb-4">Component Statistics</h3>
                                     <ul className="space-y-2 text-sm">
                                        <li className="flex justify-between"><span className="text-gray-400">Conversations:</span> <span className="text-gray-200 font-medium">{componentStats.totalConversations?.toLocaleString() || 0}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">Total Messages:</span> <span className="text-gray-200 font-medium">{componentStats.totalMessages?.toLocaleString() || 0}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">User Messages:</span> <span className="text-gray-200 font-medium">{componentStats.totalUserMessages?.toLocaleString() || 0}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">AI Messages:</span> <span className="text-gray-200 font-medium">{componentStats.totalAIMessages?.toLocaleString() || 0}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">Est. Cost (USD):</span> <span className="text-gray-200 font-medium">{formatCurrency(componentStats.totalCostUSD)}</span></li>
                                        <li className="flex justify-between"><span className="text-gray-400">Msg Rate:</span> <span className="text-gray-200 font-medium">{componentStats.messageRate || 'N/A'}</span></li>
                                     </ul>
                                 </div>
                             </div>
                        </section>

                        {/* Charts */}
                        <section ref={chartsRef} className={`transition-all duration-500 ease-out delay-250 ${isChartsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                            <h2 className="text-xl font-semibold text-gray-200 mb-5 border-b border-gray-800/50 pb-2">Usage Trends</h2>
                            {/* ... (Charts Grid - same as previous correct version) ... */}
                             <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-base font-semibold text-gray-300 mb-4">Daily Message Volume</h3>
                                     <ResponsiveContainer width="100%" height={300}>
                                         <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                                             <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                             <XAxis dataKey="day" stroke="#888" fontSize={10} />
                                             <YAxis stroke="#888" fontSize={10} />
                                             <Tooltip cursor={{ fill: 'rgba(139, 92, 246, 0.1)' }} contentStyle={{ backgroundColor: '#1f2937', borderColor: '#4b5563', borderRadius: '4px' }} labelStyle={{ color: '#d1d5db', fontSize: '12px', marginBottom: '5px' }} itemStyle={{ color: '#e5e7eb', fontSize: '12px' }}/>
                                             <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                                             <Bar dataKey="apiMessages" name="API Msgs" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                                             <Bar dataKey="componentMessages" name="Comp. Msgs" fill="#6366f1" radius={[3, 3, 0, 0]} />
                                         </BarChart>
                                     </ResponsiveContainer>
                                 </div>
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-base font-semibold text-gray-300 mb-4">Daily Estimated Cost (USD)</h3>
                                     <ResponsiveContainer width="100%" height={300}>
                                         <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                             <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                             <XAxis dataKey="day" stroke="#888" fontSize={10} />
                                             <YAxis stroke="#888" fontSize={10} tickFormatter={(value) => `$${value.toFixed(3)}`} />
                                             <Tooltip contentStyle={{ backgroundColor: '#1f2937', borderColor: '#4b5563', borderRadius: '4px' }} labelStyle={{ color: '#d1d5db', fontSize: '12px', marginBottom: '5px' }} itemStyle={{ fontSize: '12px' }} formatter={(value, name) => [formatCurrency(value), name]}/>
                                             <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                                             <Line type="monotone" dataKey="apiCost" name="API Cost" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3, fill: '#a78bfa' }} activeDot={{ r: 5 }} />
                                             <Line type="monotone" dataKey="componentCost" name="Comp. Cost" stroke="#6366f1" strokeWidth={2} dot={{ r: 3, fill: '#6366f1' }} activeDot={{ r: 5 }} />
                                         </LineChart>
                                     </ResponsiveContainer>
                                 </div>
                             </div>
                        </section>

                        {/* Additional Metrics */}
                        <section ref={metricsRef} className={`transition-all duration-500 ease-out delay-300 ${isMetricsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                            <h2 className="text-xl font-semibold text-gray-200 mb-5 border-b border-gray-800/50 pb-2">Efficiency Metrics</h2>
                            {/* ... (Metrics Grid - same as previous correct version) ... */}
                             <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-sm font-medium text-gray-400 mb-3">Avg. Messages / Conversation</h3>
                                     <div className="flex justify-around items-baseline">
                                         <div className="text-center"><p className="text-2xl font-semibold text-purple-300">{apiStats.averageMessagesPerConversation?.toFixed(1) || 'N/A'}</p><p className="text-xs text-gray-500">API</p></div>
                                         <div className="text-center"><p className="text-2xl font-semibold text-indigo-300">{componentStats.averageMessagesPerConversation?.toFixed(1) || 'N/A'}</p><p className="text-xs text-gray-500">Component</p></div>
                                     </div>
                                 </div>
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-sm font-medium text-gray-400 mb-3">Avg. Cost / Conversation</h3>
                                     <div className="flex justify-around items-baseline">
                                         <div className="text-center"><p className="text-2xl font-semibold text-purple-300">{formatCurrency(apiStats.conversationCostRange?.avg)}</p><p className="text-xs text-gray-500">API</p></div>
                                         <div className="text-center"><p className="text-2xl font-semibold text-indigo-300">{formatCurrency(componentStats.conversationCostRange?.avg)}</p><p className="text-xs text-gray-500">Component</p></div>
                                     </div>
                                 </div>
                                 <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                                     <h3 className="text-sm font-medium text-gray-400 mb-3">Conversations Today</h3>
                                     <div className="flex justify-around items-baseline">
                                         <div className="text-center"><p className="text-2xl font-semibold text-purple-300">{(apiStats.conversationsToday ?? apiStats.activeConversationsToday ?? 0).toLocaleString()}</p><p className="text-xs text-gray-500">API</p></div>
                                         <div className="text-center"><p className="text-2xl font-semibold text-indigo-300">{(componentStats.conversationsToday ?? componentStats.activeConversationsToday ?? 0).toLocaleString()}</p><p className="text-xs text-gray-500">Component</p></div>
                                     </div>
                                 </div>
                             </div>
                        </section>
                    </>
                 )}

                 {/* Fallback if stats fetched successfully but are empty/null */}
                 {!loadingStats && !statsError && (!apiStats || !componentStats) && (
                    <div className="text-center py-16 px-6 bg-gray-950 border border-dashed border-gray-800/50 rounded-lg">
                         <Info className="w-12 h-12 text-gray-700 mx-auto mb-4" strokeWidth={1}/>
                         <p className="text-gray-500">No statistics data found for <span className="font-medium text-gray-400">{selectedAgentName}</span> in the selected date range.</p>
                         <p className="text-xs text-gray-600 mt-2">Try adjusting the date range or ensure the assistant had interactions during this period.</p>
                    </div>
                 )}
            </div>
        )}
    </div>
  );
};


// --- Main Page Component (Shell using Echdeploys structure) ---
function StatsPage() {
    const pageTitle = 'Agent Analytics'; // Static title for the overall page

    return (
        <div className="flex h-screen bg-black font-sans antialiased text-gray-200">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <TopBar pageTitle={pageTitle} />
                <main className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black relative bg-black">
                    <div className="relative z-10 h-full">
                        <StatsContent />
                    </div>
                </main>
            </div>
            <style jsx global>{`
                /* Echtools global styles */
                .scrollbar-thin { scrollbar-width: thin; scrollbar-color: theme(colors.gray.700 / 0.5) transparent; }
                .scrollbar-thin::-webkit-scrollbar { width: 5px; height: 5px; }
                .scrollbar-thin::-webkit-scrollbar-thumb { background-color: theme(colors.gray.700 / 0.5); border-radius: 3px; }
                .scrollbar-thin::-webkit-scrollbar-track { background-color: transparent; }
                body { background-color: black; margin: 0; }
                @keyframes bounce-quick { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
                .animate-bounce-quick { animation: bounce-quick 0.3s ease-in-out; }
                @keyframes fade-in-fast { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
                @keyframes spin { to { transform: rotate(360deg); } }
                .animate-spin { animation: spin 1s linear infinite; }
                .line-clamp-2 { overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

                /* Custom styles for Recharts Tooltip/Legend text */
                .recharts-tooltip-label { color: #d1d5db !important; font-size: 12px !important; margin-bottom: 5px !important; }
                .recharts-tooltip-item { color: #e5e7eb !important; font-size: 12px !important; }
                .recharts-legend-item-text { color: #a0aec0 !important; /* Slightly dimmer text for legend */}
                .recharts-cartesian-axis-tick-value tspan { font-size: 10px !important; fill: #888 !important; }
            `}</style>
        </div>
    );
}

export default StatsPage;